import {
  lookupByRxNormForm,
  parseSigDosage,
  PATIENT_FRIENDLY_ROUTES,
  type PatientFriendlyRoute,
} from '@liza/fhir-utils';
import type { ValueSetCode } from '../components/records/ValueSetCombobox';

export interface DoseFormDerivation {
  /** Per-dose amount from the vendor sig, or null when no sig states one. */
  derivedDose: number | null;
  /** Patient-friendly unit for the product, or null when none could be resolved. */
  derivedUnit: string | null;
  /** Patient-friendly route (display + SNOMED code) for the product, or null. */
  derivedRoute: PatientFriendlyRoute | null;
  /** Set when a dose form resolved but `RXNORM_FORM_LOOKUP` carries no mapping for it — the mapping
   * needs adding. */
  unmappedDoseFormCode: string | undefined;
  /** Best available label for {@link unmappedDoseFormCode}. */
  unmappedDoseFormDisplay: string | undefined;
  /** Set when the terminology answered for a selected product but carried no `dose_form_of` at all —
   * a data gap in the concept itself, worth reporting even when the sig covered for it. */
  productMissingDoseForm: ValueSetCode | null;
}

const EMPTY: DoseFormDerivation = {
  derivedDose: null,
  derivedUnit: null,
  derivedRoute: null,
  unmappedDoseFormCode: undefined,
  unmappedDoseFormDisplay: undefined,
  productMissingDoseForm: null,
};

/**
 * The sig-derived route phrase, resolved against the form's option set so it carries its SNOMED code.
 *
 * Looking the phrase up (rather than returning `{ display }`) is what keeps `dosage.route` coded for
 * eRx round-trips — a display-only route would silently downgrade every sig-derived prescription to
 * text. `parseSigDosage` only ever emits phrases from this table, so the lookup hits; returning
 * `undefined` on a miss means a future rule that drifts from the vocabulary falls through to the
 * dose form rather than injecting an unselectable option into the Select.
 */
function resolveRouteOption(display: string): PatientFriendlyRoute | undefined {
  return PATIENT_FRIENDLY_ROUTES.find((route) => route.display === display);
}

/** Resolve the route, sig first (it can carry laterality a dose form can't), then the dose form. */
function resolveRoute(
  sigRoute: string | undefined,
  formEntry: { route: string; snomedRouteCode?: string } | undefined,
): PatientFriendlyRoute | null {
  const fromSig = sigRoute ? resolveRouteOption(sigRoute) : undefined;
  if (fromSig) return fromSig;
  return formEntry ? { display: formEntry.route, snomedCode: formEntry.snomedRouteCode } : null;
}

/**
 * Resolve the medication form's Dose / Dose unit / Route for the selected product, and surface the
 * terminology gaps worth reporting.
 *
 * Two independent signals, deliberately ordered:
 *
 *  1. **The ScriptSure sig** (`parseSigDosage`) — the vendor's own directions for the exact
 *     formulation ("Inject 2.5 milligram subcutaneously every seven days"). It is the richest signal:
 *     the only one that yields a **dose**, the only one that gives a liquid its
 *     concentration-independent unit (`mg` for Zepbound's `2.5 mg/0.5 mL`, not a container count), it
 *     carries laterality a dose form cannot ("in the left ear"), and it is clinically phrased by the
 *     vendor rather than inferred by us. It is also the only signal available at all when the
 *     terminology has no `dose_form_of` for the product (#5730).
 *  2. **The RxNorm dose form** (`dose_form_of` → `RXNORM_FORM_LOOKUP`) — coded and unambiguous, but
 *     unit/route only. Fills whichever fields the sig left unset, so a formulation with no saved sig
 *     template still seeds Units and Route as it always did.
 *
 * Fields are resolved independently: a sig naming a unit but no route takes its route from the dose
 * form. Anything neither signal states stays null, and the form leaves that field empty for the
 * prescriber — never a guessed default, since these values land on a prescription.
 *
 * Pure so the precedence rules and the failure modes are unit-testable without react-query.
 */
export function deriveDoseForm(args: {
  selectedProduct: ValueSetCode | null;
  /** The dose form from the terminology's `dose_form_of`, when it resolved. */
  doseForm: ValueSetCode | null;
  /** Whether the dose-form lookup has returned successfully. */
  isDoseFormResolved: boolean;
  /** The selected formulation's ScriptSure sig template, when it has one. */
  sigLine?: string | undefined;
}): DoseFormDerivation {
  const { selectedProduct, doseForm, isDoseFormResolved, sigLine } = args;

  if (!selectedProduct) return EMPTY;

  const fromSig = parseSigDosage(sigLine);

  const formEntry = doseForm?.code ? lookupByRxNormForm(doseForm.code) : undefined;
  const unmappedDoseFormCode = doseForm?.code && !formEntry ? doseForm.code : undefined;

  return {
    derivedDose: fromSig.dose ?? null,
    derivedUnit: fromSig.units ?? formEntry?.unit ?? null,
    derivedRoute: resolveRoute(fromSig.route, formEntry),
    unmappedDoseFormCode,
    unmappedDoseFormDisplay: unmappedDoseFormCode
      ? (doseForm?.display ?? selectedProduct.display ?? undefined)
      : undefined,
    // Reported even when the sig covered for it: the terminology data still needs fixing, and
    // products with no sig template get nothing at all.
    productMissingDoseForm: isDoseFormResolved && !doseForm?.code ? selectedProduct : null,
  };
}
